/* eslint-disable import/extensions */
import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  Output,
  ViewEncapsulation,
} from '@angular/core';
import {
  ChangeEvent,
  CKEditorModule,
  loadCKEditorCloud,
  type CKEditorCloudConfig,
  type CKEditorCloudResult,
} from '@ckeditor/ckeditor5-angular';
import type { EditorConfig } from 'https://cdn.ckeditor.com/typings/ckeditor5.d.ts';
import { EnvironmentService } from 'src/app/common/services/environment/environment.service';
import { stripHtmlContent } from '../../helpers/html.utilities';
import { CkEditorService } from '../../services/ck-editor/ck-editor.service';
import {
  CkUploadAdapter,
  FileRepositoryWithAdapter,
} from './ck-upload-adapter';

// Add minimal typings for RTC-related config to avoid using `any`.
interface CollaborationConfig {
  channelId: string;
}

interface EditorConfigWithRTC extends EditorConfig {
  collaboration?: CollaborationConfig;
}

interface ToolbarItem {
  label: string;
  icon: string;
  items: string[];
}

// Cloud configuration for CKEditor CDN
const cloudConfig = {
  version: '47.2.0',
  premium: true,
} satisfies CKEditorCloudConfig;

@Component({
  selector: 'app-ck-editor',
  standalone: true,
  imports: [CommonModule, CKEditorModule],
  encapsulation: ViewEncapsulation.None,
  templateUrl: './ck-editor.component.html',
  styleUrls: ['./ck-editor.component.scss'],
})
export class CkEditorComponent implements AfterViewInit, OnDestroy {
  @Input() initialData = '';

  @Input() channelId = '';

  @Input() disabled = false;

  @Input() version: 'none' | 'mini' | 'full' | 'custom' = 'none';

  @Input() rtc = false;

  @Input() minHeight = '90px';

  @Input() placeholder = 'Type something';

  @Input() debounceTime = 500;

  @Input() customToolbarItems: (string | ToolbarItem)[] | null = null;

  @Input() editorType: 'classic' | 'inline' = 'classic';

  @Output() readonly contentChange = new EventEmitter<string>();

  @Output() readonly isTyping = new EventEmitter<boolean>();

  public editorContent = '';

  private observer?: IntersectionObserver;

  private hasInitialized = false;

  constructor(
    changeDetector: ChangeDetectorRef,
    private ckeditorService: CkEditorService,
    private elementRef: ElementRef<HTMLElement>
  ) {
    this.changeDetector = changeDetector;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public Editor: any | null = null;

  public config: EditorConfigWithRTC = {};

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public editorInstance: any = null;

  private changeDetector: ChangeDetectorRef;

  private debounceTimer: ReturnType<typeof setTimeout> | undefined;

  private debounce(func: () => void, delay: number): void {
    clearTimeout(this.debounceTimer);
    this.debounceTimer = setTimeout(func, delay);
  }

  public onEditorChange(evt: ChangeEvent): void {
    let content = evt.editor.getData();
    this.editorContent = content;
    this.debounce(() => {
      // Strip HTML if version is 'none' - output plain text only
      if (this.version === 'none') {
        content = stripHtmlContent(content);
      }

      this.contentChange.emit(content);
    }, this.debounceTime);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public onReady(editor: any): void {
    this.editorInstance = editor;

    // Wire a custom upload adapter so pasted/dropped images are uploaded
    // to our backend and inserted automatically once complete.
    const repoUnknown = editor.plugins.get('FileRepository') as unknown;
    const fileRepo = repoUnknown as
      | Partial<FileRepositoryWithAdapter>
      | undefined;
    if (fileRepo) {
      fileRepo.createUploadAdapter = (loader: unknown) =>
        new CkUploadAdapter(
          loader as unknown as import('./ck-upload-adapter').CkLoader
        );
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    editor.editing.view.change((writer: any) => {
      const root = editor.editing.view.document.getRoot();
      const height = this.minHeight.trim();
      if (root) {
        writer.setStyle('min-height', height, root);
      }
    });

    editor.editing.view.document.on('keydown', () => {
      this.isTyping.emit(true);
    });

    // SEND DATA TO EVENT LOG
    this.ckeditorService
      .createEventLog({
        url: window.location.pathname,
        document_id: this.channelId,
      })
      .subscribe();
  }

  ngAfterViewInit(): void {
    if (typeof IntersectionObserver === 'undefined') {
      this.initEditor();
      return;
    }

    this.observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!this.hasInitialized && entry.isIntersecting) {
          this.hasInitialized = true;
          this.initEditor();
          this.observer?.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    this.observer.observe(this.elementRef.nativeElement);
  }

  private initEditor(): void {
    if (this.initialData !== '') {
      this.editorContent = this.initialData;
    }
    loadCKEditorCloud(cloudConfig).then((cloud) => {
      this.setupEditorWithCloudPackages(cloud);
    });
  }

  private setupEditorWithCloudPackages(
    cloud: CKEditorCloudResult<typeof cloudConfig>
  ): void {
    // Get all the plugins from the cloud packages
    const {
      ClassicEditor,
      InlineEditor,
      Alignment,
      Autoformat,
      AutoLink,
      Autosave,
      BlockQuote,
      Bold,
      CloudServices,
      Essentials,
      Emoji,
      FontBackgroundColor,
      FontColor,
      FontFamily,
      FontSize,
      Fullscreen,
      Heading,
      Highlight,
      HorizontalLine,
      Image,
      ImageCaption,
      ImageInsert,
      ImageResize,
      ImageStyle,
      ImageToolbar,
      ImageUpload,
      Indent,
      Italic,
      Link,
      List,
      ListProperties,
      MediaEmbed,
      Mention,
      Paragraph,
      PasteFromOffice,
      PictureEditing,
      RemoveFormat,
      Strikethrough,
      Subscript,
      Superscript,
      Table,
      TableCaption,
      TableCellProperties,
      TableColumnResize,
      TableProperties,
      TableToolbar,
      Underline,
      Undo,
    } = cloud.CKEditor;

    const {
      Comments,
      CaseChange,
      ExportPdf,
      ExportWord,
      ImportWord,
      PasteFromOfficeEnhanced,
      RealTimeCollaborativeComments,
      RealTimeCollaborativeEditing,
    } = cloud.CKEditorPremiumFeatures;

    // Build plugin list conditionally based on RTC flag.
    const plugins: unknown[] = [
      Alignment,
      Autoformat,
      AutoLink,
      Autosave,
      BlockQuote,
      Bold,
      CloudServices,
      Essentials,
      Emoji,
      FontColor,
      FontBackgroundColor,
      FontSize,
      FontFamily,
      Fullscreen,
      HorizontalLine,
      Heading,
      Highlight,
      Image,
      ImageCaption,
      ImageResize,
      ImageStyle,
      ImageToolbar,
      ImageInsert,
      ImageUpload,
      Indent,
      Italic,
      Link,
      List,
      ListProperties,
      MediaEmbed,
      Mention,
      Paragraph,
      PasteFromOffice,
      PictureEditing,
      RemoveFormat,
      Strikethrough,
      Table,
      TableCaption,
      TableCellProperties,
      TableColumnResize,
      TableProperties,
      TableToolbar,
      Underline,
      Undo,
      Subscript,
      Superscript,
      Comments,
      CaseChange,
      ExportPdf,
      ExportWord,
      ImportWord,
      PasteFromOfficeEnhanced,
    ];

    if (this.rtc) {
      plugins.push(RealTimeCollaborativeComments, RealTimeCollaborativeEditing);
    }

    const toolbarItems: (string | ToolbarItem)[] = [];
    // Build toolbar items based on version.
    switch (this.version) {
      case 'none':
        break;
      case 'mini':
        toolbarItems.push(
          'bold',
          'italic',
          'underline',
          '|',
          'bulletedList',
          'numberedList',
          '|',
          'emoji',
          'link'
        );
        break;
      case 'full':
        toolbarItems.push(
          'bold',
          'italic',
          'underline',
          {
            label: 'More text styles',
            icon: 'text',
            items: [
              'heading',
              'fontFamily',
              'fontSize',
              'fontColor',
              'fontBackgroundColor',
              'strikethrough',
              'superscript',
              'subscript',
              'removeFormat',
              'highlight',
            ],
          },
          '|',
          'alignment',
          '|',
          'bulletedList',
          'numberedList',
          'indent',
          'outdent',
          '|',
          'emoji',
          'link',
          'insertTable',
          'insertImage',
          'mediaEmbed',
          'blockQuote',
          'horizontalLine',
          '|',
          'undo',
          'redo',
          '|',
          'fullscreen'
        );
        break;
      case 'custom':
        if (this.customToolbarItems) {
          toolbarItems.push(...this.customToolbarItems);
        }
        break;
      default:
      // No toolbar items.
    }

    // Set the appropriate editor type
    this.Editor = this.editorType === 'inline' ? InlineEditor : ClassicEditor;

    this.config = {
      initialData: this.initialData === '' ? '<p></p>' : this.initialData,
      plugins: plugins as unknown as NonNullable<EditorConfig['plugins']>,
      toolbar: {
        items: toolbarItems,
      },
      emoji: {
        definitionsUrl: 'cdn',
      },
      comments: {
        editorConfig: {
          extraPlugins: [Bold, Italic, Underline, List, Autoformat],
        },
      },
      exportPdf: {
        stylesheets: [
          'https://cdn.ckeditor.com/ckeditor5/47.1.0/ckeditor5.css',
          'https://cdn.ckeditor.com/ckeditor5-premium-features/47.1.0/ckeditor5-premium-features.css',
        ],
        fileName: 'export-pdf-demo.pdf',
        appID: 'cke5-demos',
        converterOptions: {
          format: 'Tabloid',
          margin_top: '20mm',
          margin_bottom: '20mm',
          margin_right: '24mm',
          margin_left: '24mm',
          page_orientation: 'portrait',
        },
        tokenUrl: false,
      },
      exportWord: {
        stylesheets: [
          'https://cdn.ckeditor.com/ckeditor5/47.1.0/ckeditor5.css',
          'https://cdn.ckeditor.com/ckeditor5-premium-features/47.1.0/ckeditor5-premium-features.css',
        ],
        fileName: 'export-word-demo.docx',
        converterOptions: {
          format: 'Tabloid',
          margin_top: '20mm',
          margin_bottom: '20mm',
          margin_right: '24mm',
          margin_left: '24mm',
          orientation: 'portrait',
        },
        tokenUrl: false,
      },
      fontColor: {
        colorPicker: {
          format: 'hex',
        },
        colors: [
          { color: '#000000', label: 'Black' },
          { color: '#424242', label: 'Dim gray' },
          { color: '#666666', label: 'Gray' },
          { color: '#999999', label: 'Light gray' },
          { color: '#CCCCCC', label: 'Very light gray' },
          { color: '#FF0000', label: 'Red' },
          { color: '#FF8000', label: 'Orange' },
          { color: '#FFFF00', label: 'Yellow' },
          { color: '#80FF00', label: 'Light green' },
          { color: '#00FF00', label: 'Green' },
          { color: '#00FF80', label: 'Aqua' },
          { color: '#00FFFF', label: 'Cyan' },
          { color: '#0080FF', label: 'Light blue' },
          { color: '#0000FF', label: 'Blue' },
          { color: '#8000FF', label: 'Purple' },
        ],
      },
      fontBackgroundColor: {
        colorPicker: {
          format: 'hex',
        },
        colors: [
          { color: '#000000', label: 'Black' },
          { color: '#424242', label: 'Dim gray' },
          { color: '#666666', label: 'Gray' },
          { color: '#999999', label: 'Light gray' },
          { color: '#CCCCCC', label: 'Very light gray' },
          { color: '#FF0000', label: 'Red' },
          { color: '#FF8000', label: 'Orange' },
          { color: '#FFFF00', label: 'Yellow' },
          { color: '#80FF00', label: 'Light green' },
          { color: '#00FF00', label: 'Green' },
          { color: '#00FF80', label: 'Aqua' },
          { color: '#00FFFF', label: 'Cyan' },
          { color: '#0080FF', label: 'Light blue' },
          { color: '#0000FF', label: 'Blue' },
          { color: '#8000FF', label: 'Purple' },
        ],
      },
      fontFamily: {
        supportAllValues: true,
        options: [
          'default',
          'greycliff-cf, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, Cantarell, "Fira Sans", "Droid Sans", "Helvetica Neue", Arial, sans-serif',
          'Arial, Helvetica, sans-serif',
          'Courier New, Courier, monospace',
          'Georgia, serif',
          'Lucida Sans Unicode, Lucida Grande, sans-serif',
          'Tahoma, Geneva, sans-serif',
          'Times New Roman, Times, serif',
          'Trebuchet MS, Helvetica, sans-serif',
          'Verdana, Geneva, sans-serif',
        ],
      },
      fontSize: {
        options: [10, 12, 14, 'default', 16, 18, 20, 22],
        supportAllValues: true,
      },
      heading: {
        options: [
          {
            model: 'paragraph',
            title: 'Paragraph',
            class: 'ck-heading_paragraph',
          },
          {
            model: 'heading1',
            view: 'h1',
            title: 'Heading 1',
            class: 'ck-heading_heading1',
          },
          {
            model: 'heading2',
            view: 'h2',
            title: 'Heading 2',
            class: 'ck-heading_heading2',
          },
          {
            model: 'heading3',
            view: 'h3',
            title: 'Heading 3',
            class: 'ck-heading_heading3',
          },
          {
            model: 'heading4',
            view: 'h4',
            title: 'Heading 4',
            class: 'ck-heading_heading4',
          },
          {
            model: 'heading5',
            view: 'h5',
            title: 'Heading 5',
            class: 'ck-heading_heading5',
          },
          {
            model: 'heading6',
            view: 'h6',
            title: 'Heading 6',
            class: 'ck-heading_heading6',
          },
        ],
      },
      image: {
        toolbar: [
          'imageTextAlternative',
          '|',
          'toggleImageCaption',
          'imageStyle:inline',
          'imageStyle:block',
          'imageStyle:side',
          '|',
          'resizeImage',
        ],
      },
      mediaEmbed: {
        toolbar: ['comment'],
        previewsInData: true,
      },
      menuBar: {
        isVisible: false,
      },
      placeholder: this.placeholder,
      table: {
        contentToolbar: [
          'tableColumn',
          'tableRow',
          'mergeTableCells',
          'tableProperties',
          'tableCellProperties',
        ],
      },
      licenseKey: EnvironmentService.ckEditorLicenseKey(),
      list: {
        properties: {
          styles: true,
          startIndex: true,
          reversed: true,
        },
      },
      cloudServices: {
        tokenUrl: () => EnvironmentService.ckEditorCloudTokenUrl(),
        webSocketUrl: EnvironmentService.ckEditorWebSocketUrl(),
        bundleVersion: EnvironmentService.ckEditorBundleVersion(),
      },
      ...(this.rtc && {
        collaboration: {
          channelId:
            this.version === 'none'
              ? `${this.channelId}.plain_text`
              : this.channelId,
        },
      }),
    };

    this.changeDetector.detectChanges();
  }

  public reset(): void {
    if (this.editorInstance) {
      this.editorInstance.setData('');
    } else {
      this.editorContent = '';
    }
  }

  public ngOnDestroy(): void {
    this.observer?.disconnect();
    clearTimeout(this.debounceTimer);
  }

  // Can be used by parent components to set editor content programmatically
  public setEditorContent(content: string): void {
    if (this.editorInstance) {
      this.editorInstance.setData(content);
      this.editorContent = content;
    } else {
      // If the editor has not initialized yet, cache the content so
      // it can be used as initialData when initEditor runs.
      this.initialData = content;
      this.editorContent = content;
    }
  }

  @HostListener('window:beforeunload', ['$event'])
  public checkPendingActions(domEvt: BeforeUnloadEvent): boolean {
    if (this.editorInstance?.plugins.get('PendingActions').hasAny) {
      domEvt.preventDefault();
      domEvt.returnValue = true;
      return false;
    }

    return true;
  }
}
